import { GitCompareArrows } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Card, CardContent, CardHeader } from '~/components/ui/card';
import type {
  CoverageDetailRow,
  ProductDetailState,
} from '~/features/catalog-operations/types/catalog-operations.types';

import { CoverageBar } from '../coverage-bar';

interface Props {
  rows: Record<ProductDetailState, CoverageDetailRow[]>;
}

const STATES: ProductDetailState[] = ['added', 'changed', 'removed', 'renamed'];

/** Product-detail changes — which attributes enrichment added, changed,
 *  removed or renamed, and on how many products.
 *
 *  One card with tabs rather than four cards: `renamed` is empty on most runs,
 *  so four side-by-side cards would usually leave dead space. Counts sit on the
 *  tab labels so an empty state is visible without clicking into it.
 *
 *  The four states are mutually exclusive per (product, attribute) — a rename
 *  is derived by pairing an add with a remove and subtracting both — so the
 *  counts do not double-count. Percentages are of the diffable cohort, not the
 *  whole batch. */
export function ProductDetailChangesCard({ rows }: Props) {
  const { t } = useTranslation('catalog-operations');
  const [activeState, setActiveState] = useState<ProductDetailState>('added');
  const panelId = useId();

  const activeRows = rows[activeState];

  return (
    <Card data-testid="batch-detail-product-detail-card">
      <CardHeader className="flex flex-row items-center gap-4 p-6">
        <div className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          <GitCompareArrows className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="space-y-0.5">
          <h2 data-testid="batch-detail-product-detail-title" className="text-xl font-medium">
            {t('coverageSignals.productDetail.title')}
          </h2>
          <p className="text-muted-foreground text-sm font-light">
            {t('coverageSignals.productDetail.subtitle')}
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 px-6 pb-6">
        <SegmentedTabs
          value={activeState}
          onChange={setActiveState}
          ariaLabel={t('coverageSignals.productDetail.tabsLabel')}
          panelId={panelId}
          className="sm:max-w-none"
          testId="batch-detail-product-detail-tabs"
          // Count rides in the `badge` slot (a pill after the label) rather
          // than inline text — an empty state still reads as a "0" badge.
          options={STATES.map((state) => ({
            value: state,
            label: t(`coverageSignals.productDetail.states.${state}`),
            badge: String(rows[state].length),
          }))}
        />

        <div
          id={panelId}
          role="tabpanel"
          data-testid={`batch-detail-product-detail-panel-${activeState}`}
          className="bg-primary/4 max-h-65 overflow-y-auto rounded-xl p-3"
        >
          {activeRows.length === 0 ? (
            <p
              data-testid="batch-detail-product-detail-empty"
              className="text-muted-foreground px-3 py-6 text-center text-sm"
            >
              {t(`coverageSignals.productDetail.emptyStates.${activeState}`)}
            </p>
          ) : (
            <ul data-testid="batch-detail-product-detail-list">
              {activeRows.map((row) => (
                <li
                  key={row.label}
                  data-testid={`batch-detail-product-detail-item-${row.label}`}
                  className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto_auto] items-center gap-4 px-3 py-3"
                >
                  <span className="text-sm">{row.label}</span>
                  <CoverageBar percent={row.percent} />
                  <span className="text-muted-foreground w-20 text-right text-xs tabular-nums">
                    {t('coverageSignals.productDetail.productCount', { count: row.products })}
                  </span>
                  <span
                    data-testid={`batch-detail-product-detail-item-value-${row.label}`}
                    className="w-14 text-right text-sm font-bold tabular-nums"
                  >
                    {row.percent}%
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
